import { getGroupText } from '@/utilities/order.v2';
import { Fragment, useEffect, useMemo } from 'react';
import WhiteContainer from '../../../components/containers/WhiteContainer';
import List from '../../../components/list/List';
import Loading from '../../../components/loading/Loading';
import text from '../../../text';
import ChargeItem from './ChargeItem';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { orderV2Selectors } from '../../../redux/selectors/order.v2';
import { usePaymentContext } from '../../hooks/usePaymentContext';
import { groupBy, mapEntries } from '@larki/shared/object';

const ProductInvoice = () => {
  const { jobId, isLoadingQuote, asMember, formatPrice } = usePaymentContext();

  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const charges = useAppSelector((state) =>
    orderV2Selectors.getNonRfqCharges(state, jobId, asMember)
  );
  const rfqCharges = useAppSelector((state) =>
    orderV2Selectors.getRfqCharges(state, jobId, asMember)
  );

  const overriddenSet = useAppSelector((state) =>
    orderV2Selectors.getOverriddenSet(state, jobId, asMember)
  );

  const dataInDatabaseCharges = useMemo(
    () => charges?.filter((c) => c.group === 'data_in_database'),
    [charges]
  );

  const nonRfqCompositionLength =
    Object.values(quote?.nonRfq.composition || {}).length || 0;

  const overriddenSiteDeploymentFee = useMemo(() => {
    if (!overriddenSet.has('site_deployment_fee')) return null;
    const fees = quote?.rfq.composition.capture_services?.totals.fees ?? [];
    return fees.length > 0 ? fees[0] : null;
  }, [quote, overriddenSet]);

  const overriddenRfqGroups = useMemo(() => {
    if (overriddenSet.size === 0) return null;
    const overridden =
      rfqCharges?.filter((c) => overriddenSet.has(c.product)) ?? [];
    const byGroup =
      overridden.length > 0 ? groupBy(overridden, 'group') : new Map();

    // Fee-only override: capture_services has no charges but still needs a group to render the fee under.
    if (!byGroup.has('capture_services') && overriddenSiteDeploymentFee) {
      byGroup.set('capture_services', []);
    }

    return byGroup.size > 0 ? byGroup : null;
  }, [rfqCharges, overriddenSet, overriddenSiteDeploymentFee]);

  const overriddenCustomOrders = useMemo(() => {
    if (overriddenSet.size === 0) return [];
    return (quote?.customOrders ?? []).filter((o) =>
      overriddenSet.has(`custom_${o.id}`)
    );
  }, [quote?.customOrders, overriddenSet]);

  const hasOverriddenItems =
    (overriddenRfqGroups?.size ?? 0) > 0 ||
    overriddenCustomOrders.length > 0 ||
    !!overriddenSiteDeploymentFee;
  const show =
    isLoadingQuote || !!dataInDatabaseCharges?.length || hasOverriddenItems;

  if (!show) return null;

  let groupIndex = nonRfqCompositionLength;

  return (
    <WhiteContainer className='Invoice position-relative p-3'>
      {isLoadingQuote ? (
        <div className='position-absolute overlay-loader-container'>
          <Loading />
        </div>
      ) : null}
      {dataInDatabaseCharges.length > 0 && (
        <>
          <h6 className='green-1 font-weight--600 mb-0 mt-2 mx-2'>
            <span className='mr-1'>{'1. '}</span>
            <span>{text('dataInDatabase')}</span>
          </h6>
          <div style={{ paddingLeft: '20px', paddingRight: '16px' }}>
            <List>
              {dataInDatabaseCharges?.map((charge, i) => (
                <ChargeItem key={i} charge={charge} />
              ))}
            </List>
          </div>
        </>
      )}
      {overriddenRfqGroups
        ? mapEntries(overriddenRfqGroups, (groupCharges, group) => {
            groupIndex++;
            return (
              <Fragment key={group}>
                <h6 className='green-1 font-weight--600 mb-0 mt-3 mx-2'>
                  <span className='mr-1'>{`${groupIndex}. `}</span>
                  <span>{getGroupText(group)}</span>
                </h6>
                <div style={{ paddingLeft: '20px', paddingRight: '16px' }}>
                  <List>
                    {groupCharges.map((charge, i) => (
                      <ChargeItem key={i} charge={charge} />
                    ))}
                    {group === 'capture_services' &&
                      overriddenSiteDeploymentFee && (
                        // @ts-expect-error List.Item is not typed
                        <List.Item
                          description={
                            overriddenSiteDeploymentFee.metadata.displayName
                          }
                          value={
                            overriddenSiteDeploymentFee.amount === 'TBC'
                              ? text('toBeConfirmed')
                              : formatPrice(overriddenSiteDeploymentFee.amount)
                          }
                          className='font-14-important'
                        />
                      )}
                  </List>
                </div>
              </Fragment>
            );
          })
        : null}
      {overriddenCustomOrders.length > 0 && (
        <Fragment>
          <h6 className='green-1 font-weight--600 mb-0 mt-3 mx-2'>
            <span className='mr-1'>{`${groupIndex + 1}. `}</span>
            <span>{text('customOrder')}s</span>
          </h6>
          <div style={{ paddingLeft: '20px', paddingRight: '16px' }}>
            <List>
              {overriddenCustomOrders.map((order) => (
                // @ts-expect-error List.Item is not typed
                <List.Item
                  className='font-14-important'
                  key={order.id}
                  description={order.name}
                  value={
                    order.price === 'TBC'
                      ? text('toBeConfirmed')
                      : formatPrice(order.price)
                  }
                />
              ))}
            </List>
          </div>
        </Fragment>
      )}
    </WhiteContainer>
  );
};

export default ProductInvoice;
